import { ChangeDetectionStrategy, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/list';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ng-cloud/badger-core/models/store';
import { User } from '@ng-cloud/badger-core/models/user';
import { UserPreference } from '@ng-cloud/badger-core/models/user-preference';
import { AuthorizationService } from '@ng-cloud/badger-core/services/authorization.service';
import { LanguageService } from '@ng-cloud/badger-core/services/language.service';
import { OrganizationService } from '@ng-cloud/badger-core/services/organization.service';
import { StoreService } from '@ng-cloud/badger-core/services/store.service';
import { ThemeService } from '@ng-cloud/badger-core/services/theme.service';
import { TitleService } from '@ng-cloud/badger-core/services/title.service';
import { UserService } from '@ng-cloud/badger-core/services/user.service';

import { ToastService } from '@ng-cloud/badger-core/toastr/toastr.service';
import { FlexFillDirective, LayoutAlignDirective, LayoutDirective, LayoutGapDirective } from '@ngbracket/ngx-layout/flex';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { tap } from 'rxjs/operators';
import { HasAccessDirective } from '../../common/directives/has-access.directive';
import { TranslationLoaderService } from '../../translation/translation-loader.service';
import { UserFormComponent } from '../user-form/user-form.component';

interface LanguageOption {
  code: string;
  label: string;
  flag: string;
}

@Component({
  selector: 'bt-user-detail',
  templateUrl: './user-detail.component.html',
  styleUrls: ['./user-detail.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FlexFillDirective,
    HasAccessDirective,
    LayoutAlignDirective,
    LayoutDirective,
    LayoutGapDirective,
    MatButton,
    MatDivider,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatTooltip,
    TranslatePipe
  ]
})
export class UserDetailComponent implements OnInit {
  id: number;
  user: User;
  currentUserId: number;
  roleOptions: string[];
  currentTheme: string;
  userHomeStore: Store;
  userPreferences: UserPreference;

  allLanguageOptions: LanguageOption[] = [
    { code: 'en', label: 'English', flag: 'us' },
    { code: 'de', label: 'Deutsch', flag: 'de' },
    { code: 'es', label: 'Español', flag: 'es' },
    { code: 'fr', label: 'Français', flag: 'fr' }
  ];

  supportedLanguages: LanguageOption[] = [];

  currentLanguage: LanguageOption;

  constructor(
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private userService: UserService,
    private organizationService: OrganizationService,
    private authService: AuthorizationService,
    private storeService: StoreService,
    private themeService: ThemeService,
    private titleService: TitleService,
    private router: Router,
    private translate: TranslateService,
    private translationLoader: TranslationLoaderService,
    private languageService: LanguageService,
    private toastr: ToastService
  ) {
    this.themeService.themes().subscribe((theme) => {
      this.currentTheme = theme;
    });

    const allowedLanguages = this.translationLoader.getAvailableLanguages();

    this.supportedLanguages = this.allLanguageOptions.filter((languageOption) =>
      allowedLanguages.includes(languageOption.code as any)
    );

    // Set current language from UserPreference. If it does not exist, use 'en' instead.
    this.languageService.languages().subscribe((language) => {
      this.currentLanguage =
        this.supportedLanguages.find(
          (supportedLanguageOption) => supportedLanguageOption.code === language
        ) || this.supportedLanguages[0];
      this.translate.use(this.currentLanguage.code);
    });
  }

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.authService.validateToken().subscribe(() => {
        this.authService.getCurrentUser().subscribe((currentUserData) => {
          if (
            currentUserData.roles.includes('admin') ||
            currentUserData.roles.includes('organization_admin')
          ) {
            this.userService
              .getRoles()
              .subscribe((roles) => (this.roleOptions = roles));
          }
          this.currentUserId = currentUserData.id;
          this.id = +params.get('id') || this.currentUserId;

          this.userService
            .getUser(this.id, { include_allowed_store_data: 'true' })
            .pipe(
              tap((user) => {
                this.titleService.setTitle(user.uid);
                if (user.organizationId) {
                  this.organizationService.addOrganization(user).subscribe();
                }

                if (user.homeStoreId) {
                  this.storeService
                    .getStore(user.homeStoreId)
                    .subscribe((store) => {
                      this.userHomeStore = store;
                    });
                }
              })
            )
            .subscribe({
              next: (currentUser) => {
                this.user = currentUser;
              },
              error: () => this.router.navigate(['/'])
            });
        });
      });
    });
  }

  openEdit(): void {
    const dialogRef = this.dialog.open(UserFormComponent, {
      data: { user: this.user, roleOptions: this.roleOptions }
    });
    dialogRef
      .afterClosed()
      .subscribe((result) => (this.user = result || this.user));
  }

  getRoles() {
    return this.user.roles.length === 0 ? 'N/A' : _.join(this.user.roles, ', ');
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  switchLang(lang: any) {
    this.currentLanguage = lang;
    localStorage.setItem('appLang', lang.code);
    this.languageService.setLangToPreferences(lang.code);
    this.translate.use(this.currentLanguage.code);
  }

  async signOut(): Promise<void> {
    try {
      this.authService.signOut();
      // noinspection JSIgnoredPromiseFromCall
      await this.router.navigate(['/']);
    } catch (error) {
      console.error('Error during sign out:', error);
      const errorMsg = this.translate.instant('signOut.error');
      this.toastr.error(errorMsg);
    }
  }
}
